import { useState, useEffect } from 'react'
import { Plus, Edit2, Trash2, RefreshCw, MapPin } from 'lucide-react'
import { adminAPI } from '../services/api'

const Regions = () => {
  const [regions, setRegions] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [modalMode, setModalMode] = useState('create') // 'create', 'edit'
  const [selectedRegion, setSelectedRegion] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    is_active: true
  })
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0
  })

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      const [regionsResponse, statsResponse] = await Promise.all([
        adminAPI.getRegions(),
        adminAPI.getRegionStats()
      ])
      
      if (regionsResponse.data.success) {
        setRegions(regionsResponse.data.regions)
      }
      
      if (statsResponse.data.success) {
        setStats(statsResponse.data.stats)
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateRegion = () => {
    setFormData({ name: '', is_active: true })
    setModalMode('create')
    setSelectedRegion(null)
    setShowModal(true)
  }

  const handleEditRegion = (region) => {
    setFormData({
      name: region.name,
      is_active: region.is_active
    })
    setModalMode('edit')
    setSelectedRegion(region)
    setShowModal(true)
  }

  const handleDeleteRegion = async (region) => {
    if (window.confirm(`Ви впевнені, що хочете видалити регіон "${region.name}"?`)) {
      try {
        await adminAPI.deleteRegion(region.id)
        fetchData()
      } catch (error) {
        console.error('Error deleting region:', error)
        alert('Помилка при видаленні регіону')
      }
    }
  }

  const handleModalClose = () => {
    setShowModal(false)
    setSelectedRegion(null)
    setFormData({ name: '', is_active: true })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.name.trim()) {
      alert('Назва регіону не може бути порожньою')
      return
    }

    try {
      if (modalMode === 'create') {
        await adminAPI.createRegion({ name: formData.name })
      } else {
        await adminAPI.updateRegion(selectedRegion.id, formData)
      }
      
      fetchData()
      handleModalClose()
    } catch (error) {
      console.error('Error saving region:', error)
      alert('Помилка при збереженні регіону: ' + (error.response?.data?.error || error.message))
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('uk-UA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusBadge = (isActive) => {
    return isActive ? 'badge badge-success' : 'badge badge-error'
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Регіони</h1>
          <p className="text-gray-600">Управління регіонами користувачів</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchData}
            disabled={loading}
            className="btn btn-secondary flex items-center space-x-2"
          >
            <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
            <span>Оновити</span>
          </button>
          <button
            onClick={handleCreateRegion}
            className="btn btn-primary flex items-center space-x-2"
          >
            <Plus size={20} />
            <span>Додати регіон</span>
          </button>
        </div>
      </div>


      {/* Список регіонів */}
      <div className="card p-6">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Назва</th>
                    <th>Статус</th>
                    <th>Дата створення</th>
                    <th>Дата оновлення</th>
                    <th>Дії</th>
                  </tr>
                </thead>
                <tbody>
                  {regions.map((region) => (
                    <tr key={region.id}>
                      <td className="font-medium">{region.id}</td>
                      <td className="font-medium">{region.name}</td>
                      <td>
                        <span className={getStatusBadge(region.is_active)}>
                          {region.is_active ? 'Активний' : 'Неактивний'}
                        </span>
                      </td>
                      <td>{formatDate(region.created_at)}</td>
                      <td>{formatDate(region.updated_at)}</td>
                      <td>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEditRegion(region)}
                            className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                            title="Редагувати"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteRegion(region)}
                            className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                            title="Видалити"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {regions.length === 0 && (
              <div className="text-center py-8">
                <MapPin className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Немає регіонів</h3>
                <p className="mt-1 text-sm text-gray-500">Почніть з додавання нового регіону.</p>
                <div className="mt-6">
                  <button
                    onClick={handleCreateRegion}
                    className="btn btn-primary"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Додати регіон
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Модальне вікно */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {modalMode === 'create' ? 'Додати регіон' : 'Редагувати регіон'}
              </h2>
              <button
                onClick={handleModalClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Назва регіону
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Київ"
                  className="input"
                  required
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">
                  Активний регіон
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleModalClose}
                  className="btn btn-secondary"
                >
                  Скасувати
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                >
                  {modalMode === 'create' ? 'Створити' : 'Зберегти'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default Regions
